import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { fetchImageUrl } from './api'
import { useAppArtworkUrls } from './useAppArtworkUrls'
import {
  calculateDpi,
  getDpiQuality,
  isArtworkOversized,
  recommendedPrintDimensions,
} from './artworkQuality'
import {
  artworkSizeGroups,
  normalizeArtworkConfig,
  updateArtworkSizeGroup,
} from './artworkSource'
import { summarizeRequestedArtwork } from './draftState'
import GuidedArtworkSourceCard from './GuidedArtworkSourceCard'
import { PreparingArtwork, UploadProgress } from './GuidedWelcome'
import ShopDesignsPicker from './ShopDesignsPicker'

function positiveNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : null
}

function sourceName(image) {
  return image?.originalFilename || 'Artwork'
}

function lowDpiArtwork(images, imageConfigs, arrangementSheetSize) {
  return (images || []).flatMap(image => {
    const config = normalizeArtworkConfig(imageConfigs?.[image.id], image)
    if (!config.included) return []

    return artworkSizeGroups(config, image).flatMap((group, groupIndex) => {
      if (
        !positiveNumber(group.printWidthIn) ||
        !positiveNumber(group.printHeightIn) ||
        isArtworkOversized(group.printWidthIn, group.printHeightIn, arrangementSheetSize)
      ) return []

      const dpi = calculateDpi(image.widthPx, image.heightPx, group.printWidthIn, group.printHeightIn)
      const recommendedSize = recommendedPrintDimensions(image.widthPx, image.heightPx)
      if (getDpiQuality(dpi) !== 'poor' || !recommendedSize) return []

      return [{ image, config: group, groupIndex, dpi, recommendedSize }]
    })
  })
}

function UploadArtworkIcon() {
  return (
    <svg aria-hidden="true" className="guided-setup__upload-icon" viewBox="0 0 24 24">
      <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
    </svg>
  )
}

function useSourceImageUrls(images, vendorToken, builderSessionId, builderDraftPublicId) {
  const [urls, setUrls] = useState({})
  const fallbacksRef = useRef(new Map())
  const attemptedRef = useRef(new Set())
  const requestsRef = useRef(new Set())
  const { getAppUrl, keepUrl, releaseUrl } = useAppArtworkUrls(images, vendorToken, builderSessionId, builderDraftPublicId)
  const keyFor = image => `${image.id}:${image.backgroundRevision ?? 0}`

  useEffect(() => () => {
    requestsRef.current.forEach(controller => controller.abort())
    requestsRef.current.clear()
    fallbacksRef.current.clear()
    attemptedRef.current.clear()
  }, [vendorToken, builderSessionId, builderDraftPublicId])

  const recover = useCallback(image => {
    const key = keyFor(image)
    if (attemptedRef.current.has(key)) return
    attemptedRef.current.add(key)
    const controller = new AbortController()
    requestsRef.current.add(controller)
    getAppUrl(image, controller.signal).then(url => {
      if (controller.signal.aborted) { releaseUrl(url); return }
      fallbacksRef.current.set(key, url)
      keepUrl(image.id, url)
      setUrls(previous => ({ ...previous, [image.id]: url }))
    }).catch(() => {}).finally(() => requestsRef.current.delete(controller))
  }, [getAppUrl, keepUrl, releaseUrl])

  useEffect(() => {
    let cancelled = false

    ;(images || []).forEach(image => {
      const existingUrl = fallbacksRef.current.get(keyFor(image)) || image.previewUrl || image.imageUrl
      if (existingUrl) {
        setUrls(previous => previous[image.id] === existingUrl
          ? previous
          : { ...previous, [image.id]: existingUrl })
        return
      }
      if (image.processingStatus !== 'processed' && image.processingStatus !== undefined) return

      fetchImageUrl(vendorToken, builderSessionId, image.id, builderDraftPublicId)
        .then(url => {
          if (!cancelled) setUrls(previous => ({
            ...previous, [image.id]: fallbacksRef.current.get(keyFor(image)) || url,
          }))
        })
        .catch(() => {})
    })

    return () => { cancelled = true }
  }, [builderDraftPublicId, builderSessionId, images, vendorToken])

  return { urls, recover }
}

function SheetSizeSelector({
  sheetSizes,
  selectedSheetSizeId,
  onSelectedSheetSizeIdChange,
  pricingSettings,
}) {
  const dynamic = pricingSettings?.pricing_mode === 'per_square_foot'
  const sortedSizes = useMemo(() => (
    [...(sheetSizes || [])].sort((a, b) => (a.width_in * a.height_in) - (b.width_in * b.height_in))
  ), [sheetSizes])

  if (dynamic) {
    return (
      <section className="guided-setup__sheet" aria-labelledby="guided-sheet-title">
        <div>
          <p className="guided-setup__section-kicker">Automatic sheet</p>
          <h2 id="guided-sheet-title">{pricingSettings.width_inches}&quot; wide · height calculated for you</h2>
        </div>
        <p>
          Starts at {pricingSettings.min_height_inches}&quot; and rounds up in {pricingSettings.height_increment_inches}&quot; increments.
          {' '}When artwork exceeds {pricingSettings.max_height_inches}&quot;, we’ll start another sheet.
          {' '}You’ll see the final dimensions and price after arranging.
        </p>
      </section>
    )
  }

  return (
    <section className="guided-setup__sheet" aria-labelledby="guided-sheet-title">
      <div>
        <p className="guided-setup__section-kicker">Sheet size</p>
        <h2 id="guided-sheet-title">Choose your gang sheet</h2>
      </div>
      <div className="guided-setup__sheet-options" role="radiogroup" aria-label="Gang sheet size">
        {sortedSizes.map(size => {
          const selected = Number(size.id) === Number(selectedSheetSizeId)
          return (
            <button
              className={`guided-setup__sheet-option${selected ? ' guided-setup__sheet-option--selected' : ''}`}
              key={size.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onSelectedSheetSizeIdChange?.(size.id)}
            >
              <strong>{size.label}</strong>
              <span>{size.width_in}&quot; × {size.height_in}&quot;</span>
            </button>
          )
        })}
      </div>
    </section>
  )
}

export function getGuidedSetupBlockers({ images, imageConfigs, arrangementSheetSize, uploads }) {
  const blockers = []
  const sourceImages = images || []

  if (uploads?.uploadingFiles?.length > 0) blockers.push('Wait for uploads to finish.')
  sourceImages
    .filter(image => image.processingStatus === 'pending')
    .forEach(image => blockers.push(`${sourceName(image)} is still processing.`))
  sourceImages
    .filter(image => image.processingStatus === 'failed')
    .forEach(image => blockers.push(`${sourceName(image)} could not be processed. Retry or remove it.`))
  sourceImages
    .filter(image => image.backgroundProcessingStatus === 'processing')
    .forEach(image => blockers.push(`${sourceName(image)} background is still processing.`))
  sourceImages
    .filter(image => image.backgroundProcessingStatus === 'failed')
    .forEach(image => blockers.push(`${sourceName(image)} background could not be removed. Review it or keep the original.`))

  const included = sourceImages.filter(image => normalizeArtworkConfig(imageConfigs?.[image.id], image).included)
  if (included.length === 0) blockers.push('Include at least one design before building.')
  included.forEach(image => {
    const config = normalizeArtworkConfig(imageConfigs?.[image.id], image)
    const groups = artworkSizeGroups(config, image)
    groups.forEach((group, groupIndex) => {
      const label = groups.length > 1 ? `${sourceName(image)} size ${groupIndex + 1}` : sourceName(image)
      if (!positiveNumber(group.printWidthIn) || !positiveNumber(group.printHeightIn)) {
        blockers.push(`${label} needs valid print dimensions.`)
      } else if (isArtworkOversized(group.printWidthIn, group.printHeightIn, arrangementSheetSize)) {
        blockers.push(`${label} is too large for the selected sheet.`)
      }
    })
  })

  return blockers
}

export default function GuidedArtworkSetup({
  vendorName,
  customerPortalPath,
  vendorToken,
  builderSessionId,
  builderDraftPublicId,
  images = [],
  imageConfigs = {},
  sheetSizes = [],
  selectedSheetSizeId,
  onSelectedSheetSizeIdChange,
  pricingSettings,
  arrangementSheetSize,
  uploads,
  maxImageCount,
  vendorArtworks = [],
  vendorArtworksLoading = false,
  vendorArtworksError = null,
  shopDesignsSearch = null,
  onImportVendorArtwork,
  onImageConfigChange,
  onRemoveImage,
  onRetryImage,
  onBackgroundPreview,
  onBackgroundApply,
  onArrangeManually,
  onBuild,
  buildError,
  onStartNew,
  transitioning = false,
}) {
  const fileInputRef = useRef(null)
  const uploadButtonRef = useRef(null)
  const setupHeadingRef = useRef(null)
  const sourceListRef = useRef(null)
  const [pendingRemovalFocus, setPendingRemovalFocus] = useState(null)
  const [shopDesignsOpen, setShopDesignsOpen] = useState(false)
  const { urls: imageUrls, recover: recoverImageUrl } = useSourceImageUrls(images, vendorToken, builderSessionId, builderDraftPublicId)
  const totals = useMemo(() => summarizeRequestedArtwork(images, imageConfigs), [imageConfigs, images])
  const blockers = useMemo(() => getGuidedSetupBlockers({
    images,
    imageConfigs,
    arrangementSheetSize,
    uploads,
  }), [arrangementSheetSize, imageConfigs, images, uploads])
  const lowDpiWarnings = useMemo(
    () => lowDpiArtwork(images, imageConfigs, arrangementSheetSize),
    [arrangementSheetSize, imageConfigs, images]
  )
  const firstLowDpiWarning = lowDpiWarnings[0]
  const backgroundProcessingImages = images.filter(image => image.backgroundProcessingStatus === 'processing')
  const backgroundFailedImages = images.filter(image => image.backgroundProcessingStatus === 'failed')
  const backgroundDecisionRequired = backgroundProcessingImages.length > 0 || backgroundFailedImages.length > 0
  const artworkUpdateInProgress = backgroundProcessingImages.length > 0 || transitioning
  const backgroundProgressMessage = backgroundProcessingImages.length === 1
    ? `Removing the background from ${sourceName(backgroundProcessingImages[0])}. This usually takes a few seconds.`
    : backgroundProcessingImages.length > 1
      ? `Removing backgrounds from ${backgroundProcessingImages.length} designs. This usually takes a few seconds.`
      : null
  const artworkUpdateMessage = backgroundProgressMessage || (
    transitioning
      ? 'Loading the updated artwork. Arrangement will unlock automatically when it is ready.'
      : null
  )
  const showDpiGuidance = !artworkUpdateInProgress && !buildError && Boolean(firstLowDpiWarning) &&
    blockers.length === 0
  const actionMessage = artworkUpdateInProgress
    ? 'Artwork is updating. Layout options will unlock automatically.'
    : buildError || blockers[0]
  const atImageLimit = images.length + (uploads?.uploadingFiles?.length || 0) >= maxImageCount
  const importedVendorArtworkIds = useMemo(() => new Set(
    images.map(image => image.vendorArtworkPublicId).filter(Boolean).map(String)
  ), [images])

  useEffect(() => {
    if (!pendingRemovalFocus) return
    if (images.some(image => String(image.id) === pendingRemovalFocus.sourceId)) return

    const sourceCards = sourceListRef.current?.querySelectorAll('[data-guided-source-card]') || []
    const nextCard = sourceCards[Math.min(pendingRemovalFocus.index, sourceCards.length - 1)]
    const nextControl = nextCard?.querySelector('[data-guided-source-focus-target]:not([disabled])') ||
      nextCard?.querySelector('button:not([disabled]), input:not([disabled])')
    if (nextControl) nextControl.focus()
    else if (uploadButtonRef.current && !uploadButtonRef.current.disabled) uploadButtonRef.current.focus()
    else setupHeadingRef.current?.focus()
    setPendingRemovalFocus(null)
  }, [images, pendingRemovalFocus])

  const handleFiles = event => {
    if (event.target.files?.length) uploads?.uploadFiles?.(event.target.files)
    event.target.value = ''
  }

  const retryImage = image => {
    onRetryImage?.(image)
    fileInputRef.current?.click()
  }

  const removeImage = useCallback(async id => {
    const index = images.findIndex(image => String(image.id) === String(id))
    await onRemoveImage?.(id)
    setPendingRemovalFocus({ sourceId: String(id), index: Math.max(0, index) })
  }, [images, onRemoveImage])

  const handleStartNew = async () => {
    if (!window.confirm('Start a new gang sheet? Your current draft will be closed.')) return
    await onStartNew?.()
  }

  const resizeLowDpiArtwork = ({ image, groupIndex, recommendedSize }) => {
    const sourceConfig = imageConfigs[image.id]
    onImageConfigChange?.(image.id, updateArtworkSizeGroup(sourceConfig, groupIndex, {
      printWidthIn: recommendedSize.widthIn,
      printHeightIn: recommendedSize.heightIn,
    }, image))
  }

  return (
    <main className="guided-setup" data-testid="guided-artwork-setup">
      <header className="guided-setup__header">
        <span className="guided-setup__brand">{vendorName || 'GSPressHQ'}</span>
        <nav aria-label="Builder links">
          {customerPortalPath && <a href={customerPortalPath} target="_top">View your gang sheets</a>}
          {onStartNew && (
            <button
              className="guided-setup__start-new"
              type="button"
              title="Close this draft and start a new gang sheet"
              onClick={() => handleStartNew().catch(() => {})}
            >
              Start New
            </button>
          )}
        </nav>
      </header>

      <div className="guided-setup__workspace">
        <section className="guided-setup__intro" aria-labelledby="guided-setup-title">
          <div>
            <p className="guided-setup__section-kicker">Artwork setup</p>
            <h1 id="guided-setup-title" ref={setupHeadingRef} tabIndex="-1">Set up your artwork</h1>
            <p>Choose the print sizes and quantities you need. We’ll arrange every requested copy automatically.</p>
          </div>
          <p className="guided-setup__totals" aria-live="polite">
            <strong>{totals.uniqueDesignCount}</strong> {totals.uniqueDesignCount === 1 ? 'design' : 'designs'}
            <span aria-hidden="true"> · </span>
            <strong>{totals.requestedTotalQuantity}</strong> requested {totals.requestedTotalQuantity === 1 ? 'copy' : 'copies'}
          </p>
        </section>

        <SheetSizeSelector
          sheetSizes={sheetSizes}
          selectedSheetSizeId={selectedSheetSizeId}
          onSelectedSheetSizeIdChange={onSelectedSheetSizeIdChange}
          pricingSettings={pricingSettings}
        />

        <section className="guided-setup__artwork" aria-labelledby="guided-artwork-title">
          <div className="guided-setup__artwork-heading">
            <div>
              <p className="guided-setup__section-kicker">Designs</p>
              <h2 id="guided-artwork-title">Your artwork</h2>
            </div>
            {onImportVendorArtwork && (
              <button
                className="gs-btn-secondary"
                type="button"
                aria-expanded={shopDesignsOpen}
                aria-controls="guided-shop-designs"
                onClick={() => setShopDesignsOpen(open => !open)}
              >
                Shop designs
              </button>
            )}
          </div>

          {shopDesignsOpen && (
            <div id="guided-shop-designs">
              <ShopDesignsPicker
                artworks={vendorArtworks}
                loading={vendorArtworksLoading}
                error={vendorArtworksError}
                importedIds={importedVendorArtworkIds}
                search={shopDesignsSearch}
                atImageLimit={atImageLimit}
                onImport={onImportVendorArtwork}
                idPrefix="guided-shop-designs"
              />
            </div>
          )}

          <section
            className={`guided-setup__upload-prompt${atImageLimit ? ' guided-setup__upload-prompt--disabled' : ''}`}
            data-testid="guided-setup-upload-prompt"
            aria-labelledby="guided-setup-upload-title"
            aria-describedby="guided-setup-upload-description guided-setup-upload-limits"
          >
            <UploadArtworkIcon />
            <div className="guided-setup__upload-copy">
              <h3 id="guided-setup-upload-title">
                {atImageLimit ? 'Artwork limit reached' : 'Add more artwork'}
              </h3>
              <p id="guided-setup-upload-description">
                {atImageLimit
                  ? `This gang sheet can include up to ${maxImageCount} artwork ${maxImageCount === 1 ? 'file' : 'files'}. Remove a design or finish this gang sheet and start another.`
                  : 'Drag and drop files anywhere on this page, or choose files from your device.'}
              </p>
              <p className="guided-setup__upload-limits" id="guided-setup-upload-limits">
                PNG, JPG, JPEG, or WebP · 100 MB per file · 2 GB total · up to {maxImageCount} artwork files
              </p>
            </div>
            <input
              ref={fileInputRef}
              className="guided-setup__file-input"
              data-testid="guided-setup-file-input"
              aria-label="Choose additional artwork files"
              type="file"
              accept=".png,.jpg,.jpeg,.webp"
              multiple
              disabled={atImageLimit}
              onChange={handleFiles}
            />
            <button
              ref={uploadButtonRef}
              className="gs-btn-secondary guided-setup__upload-button"
              type="button"
              disabled={atImageLimit}
              onClick={() => fileInputRef.current?.click()}
            >
              {atImageLimit ? 'Artwork limit reached' : 'Choose artwork files'}
            </button>
          </section>

          {(uploads?.uploadErrors?.length || 0) > 0 && (
            <ul className="guided-welcome__errors" role="alert">
              {uploads.uploadErrors.map((error, index) => <li key={`${error}-${index}`}>{error}</li>)}
            </ul>
          )}
          <UploadProgress uploadingFiles={uploads?.uploadingFiles || []} />
          <PreparingArtwork images={images} />
          {artworkUpdateInProgress && (
            <div
              className="guided-setup__artwork-update"
              data-testid="guided-artwork-update"
              role="status"
              aria-live="polite"
            >
              <span className="guided-setup__artwork-update-spinner" aria-hidden="true" />
              <div>
                <strong>
                  {backgroundProcessingImages.length > 0
                    ? 'Updating artwork…'
                    : 'Loading updated artwork…'}
                </strong>
                <p>{artworkUpdateMessage}</p>
              </div>
            </div>
          )}

          <div className="guided-setup__source-list" ref={sourceListRef}>
            {images.map(image => (
              <GuidedArtworkSourceCard
                key={image.id}
                image={image}
                imageUrl={imageUrls[image.id]}
                onImageError={() => recoverImageUrl(image)}
                config={imageConfigs[image.id]}
                arrangementSheetSize={arrangementSheetSize}
                onConfigChange={onImageConfigChange}
                onRemove={removeImage}
                onRetry={retryImage}
                onBackgroundPreview={onBackgroundPreview}
                onBackgroundApply={onBackgroundApply}
              />
            ))}
          </div>
        </section>
      </div>

      <footer className="guided-setup__actions">
        <div>
          {showDpiGuidance && (
            <div className="guided-setup__dpi-blocker" role="alert">
              <strong>
                {lowDpiWarnings.length === 1
                  ? 'Low DPI detected — print at your own risk'
                  : `Low DPI detected in ${lowDpiWarnings.length} designs — print at your own risk`}
              </strong>
              <p className="guided-setup__dpi-details">
                {sourceName(firstLowDpiWarning.image)}{firstLowDpiWarning.groupIndex > 0
                  ? ` size ${firstLowDpiWarning.groupIndex + 1}`
                  : ''} is {firstLowDpiWarning.dpi} DPI at{' '}
                {firstLowDpiWarning.config.printWidthIn}&quot; × {firstLowDpiWarning.config.printHeightIn}&quot;.
                {' '}It may print blurry or pixelated.{' '}
                <button
                  className="guided-setup__dpi-resize"
                  type="button"
                  onClick={() => resizeLowDpiArtwork(firstLowDpiWarning)}
                >
                  Resize {sourceName(firstLowDpiWarning.image)} to 300 DPI ({firstLowDpiWarning.recommendedSize.widthIn}&quot; ×{' '}
                  {firstLowDpiWarning.recommendedSize.heightIn}&quot;)
                </button>
                {' '}or continue with the current size.
              </p>
            </div>
          )}
          {actionMessage && (
            <p className="guided-setup__build-message" role={actionMessage === buildError ? 'alert' : 'status'}>
              {actionMessage}
            </p>
          )}
        </div>
        <button
          className="gs-btn-secondary"
          type="button"
          disabled={backgroundDecisionRequired || transitioning}
          onClick={onArrangeManually}
        >
          Arrange manually
        </button>
        <button className="gs-btn-primary" type="button" disabled={blockers.length > 0 || transitioning} onClick={onBuild}>
          {artworkUpdateInProgress ? 'Preparing artwork…' : buildError ? 'Retry build' : 'Build my gang sheet'}
        </button>
      </footer>
    </main>
  )
}
